// Tokens y piezas visuales compartidas del wizard (mismo estilo inline que
// src/pages/Tools/AltitudeCustomerCorrection.js).
import React from 'react';
import { AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle, AiOutlineInfoCircle } from 'react-icons/ai';

export const C = {
    ink: '#0F172A',
    text: '#334155',
    muted: '#475569',
    faint: '#64748B',
    line: '#E2E8F0',
    lineStrong: '#CBD5E1',
    bg: '#F8FAFC',
    card: '#FFFFFF',
    primary: '#2563EB',
    primaryDark: '#1E40AF',
    ok: '#166534',
    okBg: '#F0FDF4',
    okLine: '#BBF7D0',
    warn: '#92400E',
    warnBg: '#FFFBEB',
    warnLine: '#FDE68A',
    err: '#991B1B',
    errBg: '#FEF2F2',
    errLine: '#FECACA',
    info: '#1E3A8A',
    infoBg: '#EFF6FF',
    infoLine: '#BFDBFE',
};

export const BTN_PRIMARY = { background: C.primary, border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.65rem 1.1rem', fontSize: '0.9rem', cursor: 'pointer', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' };
export const BTN_SECONDARY = { background: '#FFFFFF', border: `1px solid ${C.lineStrong}`, borderRadius: '8px', color: C.ink, fontWeight: 600, padding: '0.6rem 1rem', fontSize: '0.875rem', cursor: 'pointer', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' };
export const BTN_DANGER_LINK = { background: 'none', border: 'none', color: C.err, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', padding: '0.5rem 0', textDecoration: 'underline' };
export const BTN_DISABLED = { background: '#94A3B8', cursor: 'not-allowed', color: '#FFFFFF', border: 'none' };
export const INPUT = { border: `1px solid ${C.lineStrong}`, borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '1rem', width: '100%', background: '#FFFFFF', color: C.ink, minHeight: '44px' };
export const INPUT_ERR = { ...INPUT, border: '1.5px solid #EF4444' };
export const INPUT_RO = { ...INPUT, background: '#F1F5F9', color: C.muted };
export const LABEL = { display: 'block', fontSize: '0.8rem', color: C.muted, marginBottom: '0.3rem', fontWeight: 600 };
export const CARD = { background: C.card, border: `1px solid ${C.line}`, borderRadius: '12px', padding: '1rem' };
export const H2 = { fontSize: '1.15rem', fontWeight: 800, color: C.ink, margin: 0 };
export const SUB = { fontSize: '0.875rem', color: C.muted, margin: '0.25rem 0 0' };

export const btn = (base, disabled) => (disabled ? { ...base, ...BTN_DISABLED } : base);

const NOTICE_BASE = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.75rem 0.9rem', borderRadius: '8px', fontSize: '0.875rem', marginTop: '0.75rem', lineHeight: 1.4 };
const NOTICE_KIND = {
    info: { background: C.infoBg, border: `1px solid ${C.infoLine}`, color: C.info, Icon: AiOutlineInfoCircle },
    warn: { background: C.warnBg, border: `1px solid ${C.warnLine}`, color: C.warn, Icon: AiOutlineWarning },
    error: { background: C.errBg, border: `1px solid ${C.errLine}`, color: C.err, Icon: AiOutlineCloseCircle },
    ok: { background: C.okBg, border: `1px solid ${C.okLine}`, color: C.ok, Icon: AiOutlineCheckCircle },
};

export function Notice({ kind, children, role }) {
    const k = NOTICE_KIND[kind] || NOTICE_KIND.info;
    const Icon = k.Icon;
    return (
        <div style={{ ...NOTICE_BASE, background: k.background, border: k.border, color: k.color }} role={role || (kind === 'error' ? 'alert' : 'status')}>
            <Icon style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
            <div style={{ minWidth: 0 }}>{children}</div>
        </div>
    );
}

export function Field({ id, label, required, error, hint, children }) {
    return (
        <div>
            <label htmlFor={id} style={LABEL}>
                {label}{required ? <span style={{ color: '#DC2626' }}> *</span> : <span style={{ color: '#94A3B8', fontWeight: 400 }}> (optional)</span>}
            </label>
            {children}
            {error
                ? <div id={`${id}-err`} style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: '0.25rem' }}>{error}</div>
                : hint ? <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.25rem' }}>{hint}</div> : null}
        </div>
    );
}

// Input controlado con estilos de error/readonly y aria.
export function TextInput({ id, value, onChange, error, readOnly, ...rest }) {
    return (
        <input
            id={id}
            value={value === undefined || value === null ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-err` : undefined}
            style={readOnly ? INPUT_RO : error ? INPUT_ERR : INPUT}
            {...rest}
        />
    );
}

const STATUS_VIEW = {
    idle: { label: 'Not started', fg: C.faint, bg: '#F1F5F9', line: C.lineStrong },
    checking: { label: 'Checking Sage…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    sending: { label: 'Sending to Sage…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    verifying: { label: 'Confirming in Sage', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    saving: { label: 'Saving…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    done: { label: 'Ready', fg: C.ok, bg: C.okBg, line: C.okLine },
    unconfirmed: { label: 'Not confirmed', fg: C.warn, bg: C.warnBg, line: C.warnLine },
    failed: { label: 'Failed', fg: C.err, bg: C.errBg, line: C.errLine },
    conflict: { label: 'Conflict', fg: C.err, bg: C.errBg, line: C.errLine },
    skipped: { label: 'Skipped', fg: C.warn, bg: C.warnBg, line: C.warnLine },
};
export const BUSY = ['checking', 'sending', 'verifying', 'saving'];

export function StatusChip({ status, attempt, total }) {
    const v = STATUS_VIEW[status] || STATUS_VIEW.idle;
    const label = status === 'verifying' && attempt ? `${v.label} (${attempt}/${total})` : v.label;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, color: v.fg, background: v.bg, border: `1px solid ${v.line}`, whiteSpace: 'nowrap' }}>
            {v.spin && <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} aria-hidden="true"></span>}
            {label}
        </span>
    );
}

// Fila de tarea: título + chip + mensaje + acción de reintento.
export function TaskRow({ title, detail, task, total, onRetry, retryLabel, extra }) {
    const busy = BUSY.indexOf(task.status) >= 0;
    const retryable = ['failed', 'unconfirmed'].indexOf(task.status) >= 0;
    return (
        <div style={{ borderTop: `1px solid ${C.line}`, padding: '0.75rem 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.ink, fontSize: '0.9rem' }}>{title}</div>
                    {detail && <div style={{ color: C.faint, fontSize: '0.8rem', wordBreak: 'break-word' }}>{detail}</div>}
                </div>
                <StatusChip status={task.status} attempt={task.attempt} total={total} />
            </div>
            {task.alreadyInSage && task.status === 'done' && <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.25rem' }}>Already existed in Sage — nothing was resent.</div>}
            {task.message && !busy && <Notice kind={task.status === 'unconfirmed' ? 'warn' : 'error'}>{task.message}</Notice>}
            {retryable && onRetry && (
                <button type="button" style={{ ...BTN_SECONDARY, marginTop: '0.5rem' }} onClick={onRetry}>{retryLabel || 'Retry'}</button>
            )}
            {extra}
        </div>
    );
}

export const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: '8px',
        minHeight: '44px',
        fontSize: '1rem',
        borderColor: state.isFocused ? C.primary : C.lineStrong,
        boxShadow: state.isFocused ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
